import { ChevronDown, ChevronUp, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useEmployees } from '../context/EmployeeContext';
import { Employee } from '../data/employeeData';

interface OrganizationalNode {
  employee: Employee;
  children: OrganizationalNode[];
  level: number;
}

interface OrgCardProps {
  employee: Employee;
  level: number;
  reportsCount: number;
  isExpanded: boolean;
  hasChildren: boolean;
  onToggle: () => void;
  onClick: () => void;
}

const OrgCard: React.FC<OrgCardProps> = ({
  employee,
  level,
  reportsCount,
  isExpanded,
  hasChildren,
  onToggle,
  onClick
}) => {
  const roleStyles = (() => {
    switch (level) {
      case 0:
        return 'ring-1 ring-purple-200';
      case 1:
        return 'ring-1 ring-blue-200';
      case 2:
        return 'ring-1 ring-green-200';
      default:
        return 'ring-1 ring-gray-200';
    }
  })();

  return (
    <div className="relative flex flex-col items-center">
      <div className="relative">
        <div
          className={`relative bg-white border border-gray-200 ${roleStyles} rounded-xl shadow-sm px-6 pt-8 pb-4 text-center min-w-[220px] max-w-[240px] cursor-pointer hover:shadow-md transition`}
          onClick={onClick}
        >
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 h-12 w-12 rounded-full border-2 border-white shadow bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-700">
            {employee.initials}
          </div>
          <div className="space-y-1">
            <div className="text-sm font-semibold truncate">{employee.name}</div>
            <div className="text-xs text-gray-600 truncate">{employee.jobTitle}</div>
          </div>
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              className="mt-3 inline-flex items-center gap-1 rounded-full bg-gray-800 px-2 py-0.5 text-[11px] font-medium text-white"
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {reportsCount}
              {isExpanded ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

interface EmployeeNodeProps {
  node: OrganizationalNode;
  onEmployeeClick: (employee: Employee) => void;
  expandedPathIds: string[];
  setExpandedPathIds: (ids: string[]) => void;
  pathToHere: string[];
}

const EmployeeNode: React.FC<EmployeeNodeProps> = ({ node, onEmployeeClick, expandedPathIds, setExpandedPathIds, pathToHere }) => {
  const { employee, children } = node;
  const hasChildren = children.length > 0;
  const isExpanded = expandedPathIds.includes(employee.id);

  const handleToggle = () => {
    if (!hasChildren) return;
    const idx = expandedPathIds.indexOf(employee.id);
    if (idx !== -1) {
      if (idx === 0) return; // keep root expanded
      setExpandedPathIds(expandedPathIds.slice(0, idx));
    } else {
      setExpandedPathIds(pathToHere);
    }
  };

  return (
    <div className="flex flex-col items-center w-full">
      <OrgCard
        employee={employee}
        level={node.level}
        reportsCount={children.length}
        isExpanded={isExpanded}
        hasChildren={hasChildren}
        onToggle={handleToggle}
        onClick={() => onEmployeeClick(employee)}
      />

      {hasChildren && isExpanded && (
        <div className="w-full">
          <div className="flex flex-col items-stretch mt-4">
            <div className="h-5 w-0.5 bg-gray-300 mx-auto"></div>
            <div className="border-t border-gray-300 mx-6"></div>
          </div>

          <div className="mt-4 flex flex-row flex-wrap items-start justify-center gap-6">
            {children.map((child) => (
              <div key={child.employee.id} className="flex flex-col items-center">
                <div className="h-5 w-0.5 bg-gray-300"></div>
                <EmployeeNode
                  node={child}
                  onEmployeeClick={onEmployeeClick}
                  expandedPathIds={expandedPathIds}
                  setExpandedPathIds={setExpandedPathIds}
                  pathToHere={[...pathToHere, child.employee.id]}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface OrganizationalChartProps {
  onEmployeeClick?: (employee: Employee) => void;
  className?: string;
}

const OrganizationalChart: React.FC<OrganizationalChartProps> = ({
  onEmployeeClick = () => {},
  className = ''
}) => {
  const { getOrganizationalHierarchy, getOrgStats } = useEmployees();
  const hierarchy = getOrganizationalHierarchy();
  const stats = getOrgStats();
  const [expandedPathIds, setExpandedPathIds] = useState<string[]>([]);

  // Ensure CEO (root) is expanded by default
  useEffect(() => {
    const rootId = hierarchy[0]?.employee.id;
    if (!rootId) return;
    setExpandedPathIds(prev => (prev.length === 0 ? [rootId] : prev));
  }, [hierarchy]);

  return (
    <div className={`bg-white ${className}`}>
      <div className="relative overflow-hidden rounded-t-xl" style={{background: 'linear-gradient(135deg,#1ecde1 0%,#0ab0bd 100%)'}}>
        <div className="px-6 py-4">
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              <h1 className="text-lg font-semibold">View Org Chart</h1>
            </div>
            <div className="text-sm opacity-90">
              {stats.totalEmployees} employees
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
          <div className="overflow-x-auto">
            <div className="min-w-max px-4 py-8">
              {hierarchy.map((node) => (
                <EmployeeNode
                  key={node.employee.id}
                  node={node}
                  onEmployeeClick={onEmployeeClick}
                  expandedPathIds={expandedPathIds}
                  setExpandedPathIds={setExpandedPathIds}
                  pathToHere={[node.employee.id]}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationalChart;
